import React, { useState, useEffect, useMemo, useCallback } from "react";
// Hareketli durum ikonları (#233) — sürekli ("always") oynar, "hareketi azalt"a uyar
import {
  UsersIcon,
  ServiceToolIcon,
  PartsBoxIcon,
  TestBeakerIcon,
  PlaymakerTruckIcon,
  ChipIcon,
  MapPinDropIcon,
} from "../../atoms/animatedIcons";
import DeviceStatusDrawer from "../DeviceStatusDrawer";
import DeviceMapDrawer from "../DeviceMapDrawer";
import { useAlert } from "../../../context/AlertContext";

// Kompakt çip paleti — PageHeader stat çipleriyle aynı dil.
// Renkler Firmalar sayfası kart paletiyle uyumlu: müşteri=blue, servis=amber,
// depo=purple, test=emerald, plasiyer=indigo
const CHIP_COLORS = {
  blue: { bg: "bg-blue-50/80", border: "border-blue-100/80", hover: "hover:bg-blue-100/80", icon: "text-blue-500", value: "text-blue-800", label: "text-blue-500" },
  amber: { bg: "bg-amber-50/80", border: "border-amber-100/80", hover: "hover:bg-amber-100/80", icon: "text-amber-500", value: "text-amber-800", label: "text-amber-500" },
  purple: { bg: "bg-purple-50/80", border: "border-purple-100/80", hover: "hover:bg-purple-100/80", icon: "text-purple-500", value: "text-purple-800", label: "text-purple-500" },
  emerald: { bg: "bg-emerald-50/80", border: "border-emerald-100/80", hover: "hover:bg-emerald-100/80", icon: "text-emerald-500", value: "text-emerald-800", label: "text-emerald-500" },
  indigo: { bg: "bg-indigo-50/80", border: "border-indigo-100/80", hover: "hover:bg-indigo-100/80", icon: "text-indigo-500", value: "text-indigo-800", label: "text-indigo-500" },
};

// Varsayılan boş dizi SABİT referans: `= []` her render'da yeni dizi üretip
// aşağıdaki useMemo'ları ve memo'lu çipleri boşuna geçersiz kılıyordu.
const EMPTY = [];

/**
 * Tek durum çipi — memo'lu: socket'ten yalnız başka bir kategorinin sayısı
 * değişince ya da çekmecede arama yazılırken hareketli ikon yeniden çizilmez.
 */
const DeviceStatusChip = React.memo(function DeviceStatusChip({ card, onOpen }) {
  const c = CHIP_COLORS[card.color] || CHIP_COLORS.blue;
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(card)}
      className={`flex items-center gap-1.5 sm:gap-2 ${c.bg} ${c.hover} rounded-md sm:rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 border ${c.border} transition-colors`}
      title={card.title}
    >
      <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 ${c.icon}`} trigger="always" />
      <span className={`text-xs sm:text-base font-bold tabular-nums ${c.value}`}>
        {card.data?.length || 0}
      </span>
      <span className={`text-[10px] sm:text-xs font-medium hidden md:inline whitespace-nowrap ${c.label}`}>
        {card.label}
      </span>
    </button>
  );
});

/** Çekmece listesi sıralama değeri — saf; bileşen dışında (useMemo bağımlılığı olmasın) */
const getSortValue = (device, key) => {
  switch (key) {
    case "serialNo":
      return (device.serialNo || device.deviceInfo?.deviceName || "").toLowerCase();
    case "connection":
      return device.deviceInfo?.connection?.status ? 1 : 0;
    case "cupNumber":
      return Number(device.cupNumber ?? 0);
    case "firmName":
      return (device.firm?.firmName || "").toLowerCase();
    case "statusChangedAt":
      return device.statusChangedAt ? new Date(device.statusChangedAt).getTime() : Infinity;
    // Bağlanmayan cihazlar detay sütunu — son bağlantı zamanı
    case "lastConnected": {
      const lc = device.deviceInfo?.connection?.lastConnected || device.lastConnectionDate;
      return lc ? new Date(lc).getTime() : Infinity;
    }
    // Kullanılmayan cihazlar detay sütunu — son tüketim zamanı
    case "lastConsumptionDate":
      return device.lastConsumptionDate ? new Date(device.lastConsumptionDate).getTime() : Infinity;
    // Kritik kotalı cihazlar detay sütunu — kritik ürün sayısı
    case "criticalCount":
      return (device.productInfo?.productInfo || []).filter(
        (p) => p.quotaDistribution && p.quota < p.quotaWarning
      ).length;
    default:
      return "";
  }
};

function ActiveDevicesCard({
  firmDevices = EMPTY,
  testDevices = EMPTY,
  playmakerDevices = EMPTY,
  serviceDevices = EMPTY,
  storageDevices = EMPTY,
  productionDevices = EMPTY,
  criticalDevices = EMPTY,
  unConnectedDevices = EMPTY,
  unUsedDevices = EMPTY,
  isLoading,
  canSeeMap = true,
  isRefreshing = false,
}) {
  const { showWarning } = useAlert();
  const [selectedDevices, setSelectedDevices] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState({ serialNo: "", deviceName: "", firmName: "", playMakerName: "" });
  const [sort, setSort] = useState(null); // { key, dir } | null → varsayılan kategori sıralaması
  const [pageSize, setPageSize] = useState(10);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    const fixedH = 64 + 56 + 48;
    const rowH = 72;
    const available = window.innerHeight - fixedH;
    setPageSize((prev) => {
      const next = Math.max(10, Math.floor(available / rowH));
      return prev !== next ? next : prev;
    });
  }, []);

  useEffect(() => {
    setPage(1);
    setSearch({ serialNo: "", deviceName: "", firmName: "", playMakerName: "" });
    setSort(null);
  }, [selectedDevices]);

  const handleSort = (key) =>
    setSort((p) =>
      p && p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );

  // Arama değişince ilk sayfaya dön
  useEffect(() => {
    setPage(1);
  }, [search.serialNo, search.deviceName, search.firmName, search.playMakerName]);

  const deviceCards = useMemo(() => [
    { type: "firmDevices", title: "Müşterideki Cihazlar", label: "Müşteride", data: firmDevices, color: "blue", icon: UsersIcon },
    { type: "serviceDevices", title: "Servisdeki Cihazlar", label: "Serviste", data: serviceDevices, color: "amber", icon: ServiceToolIcon },
    { type: "storageDevices", title: "Depodaki Cihazlar", label: "Depoda", data: storageDevices, color: "purple", icon: PartsBoxIcon },
    { type: "testDevices", title: "Testteki Cihazlar", label: "Testte", data: testDevices, color: "emerald", icon: TestBeakerIcon },
    { type: "playmakerDevices", title: "Plasiyerdeki Cihazlar", label: "Plasiyerde", data: playmakerDevices, color: "indigo", icon: PlaymakerTruckIcon },
  ], [firmDevices, serviceDevices, storageDevices, testDevices, playmakerDevices]);

  const openCategory = useCallback((card) => {
    setSelectedDevices({ devices: card.data || [], type: card.type, title: card.title });
    setDrawerOpen(true);
  }, []);

  // Drawer içindeki tab değişimi — liste aynı drawer'da kategoriye göre değişir
  const handleTabChange = useCallback((type) => {
    const card = deviceCards.find((c) => c.type === type);
    if (card) {
      setSelectedDevices({ devices: card.data || [], type: card.type, title: card.title });
    }
  }, [deviceCards]);

  const handleMapClick = useCallback(() => {
    // Harita her durumda açılır — konumu olmayan cihazlar drawer'ın kendi
    // listesinde "Konum yok" olarak zaten işaretlenir
    if (firmDevices && firmDevices.length > 0) {
      setMapOpen(true);
    } else {
      showWarning("Müşteride cihaz bulunamadı!");
    }
  }, [firmDevices, showWarning]);

  const filteredDevices = useMemo(() => {
    const qSerial = search.serialNo.trim().toLowerCase();
    const qName = search.deviceName.trim().toLowerCase();
    const qFirm = search.firmName.trim().toLowerCase();
    const qPlayMaker = search.playMakerName.trim().toLowerCase();
    const filtered = (selectedDevices?.devices || []).filter((device) => {
      const serial = (device.serialNo || "").toLowerCase();
      const name = (device.deviceInfo?.deviceName || device.deviceName || "").toLowerCase();
      const firm = (device.firm?.firmName || "").toLowerCase();
      const playMaker = device.playMaker
        ? `${device.playMaker.name || ""} ${device.playMaker.lastName || ""}`.trim().toLowerCase()
        : "";
      return (
        serial.includes(qSerial) &&
        name.includes(qName) &&
        firm.includes(qFirm) &&
        playMaker.includes(qPlayMaker)
      );
    });
    const type = selectedDevices?.type;
    if (sort) {
      // Kullanıcı sütun başlığıyla sıralama seçtiyse o geçerli
      const mul = sort.dir === "asc" ? 1 : -1;
      filtered.sort((a, b) => {
        const av = getSortValue(a, sort.key);
        const bv = getSortValue(b, sort.key);
        if (typeof av === "string" || typeof bv === "string") {
          return String(av).localeCompare(String(bv), "tr") * mul;
        }
        return (av - bv) * mul;
      });
    } else if ((type === "serviceDevices" || type === "playmakerDevices") && filtered.length > 0) {
      filtered.sort((a, b) => {
        const dateA = a.statusChangedAt ? new Date(a.statusChangedAt).getTime() : Infinity;
        const dateB = b.statusChangedAt ? new Date(b.statusChangedAt).getTime() : Infinity;
        return dateA - dateB;
      });
    }
    return filtered;
  }, [selectedDevices, search, sort]);

  const pagedDevices = filteredDevices.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {/* Kompakt üst bar — yenile + başlık + kategori çipleri + harita */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg sm:rounded-xl shadow-sm border border-indigo-100">
              <ChipIcon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" trigger="always" />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
              Dashboard
            </h3>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-1 justify-end flex-wrap">
            {isLoading || isRefreshing
              ? Array.from({ length: 5 }).map((_, idx) => (
                  <span
                    key={idx}
                    className="inline-block h-8 sm:h-9 w-20 sm:w-28 bg-gray-100 border border-gray-200 rounded-md sm:rounded-lg animate-pulse"
                  />
                ))
              : deviceCards.map((card) => (
                  <DeviceStatusChip key={card.type} card={card} onOpen={openCategory} />
                ))}

            {!isLoading && !isRefreshing && canSeeMap && (
              <button
                onClick={handleMapClick}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md sm:rounded-lg text-gray-600 hover:text-gray-800 transition-all duration-300 shadow-sm"
                title="Müşterideki cihazları haritada göster"
              >
                <MapPinDropIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" trigger="always" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                  Cihaz Konumları
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <DeviceStatusDrawer
        selectedDevices={selectedDevices}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        pagedDevices={pagedDevices}
        page={page}
        setPage={setPage}
        filteredDevices={filteredDevices}
        pageSize={pageSize}
        search={search}
        setSearch={setSearch}
        sort={sort}
        onSort={handleSort}
        tabs={deviceCards.map((c) => ({
          type: c.type,
          label: c.label,
          count: c.data?.length || 0,
        }))}
        onTabChange={handleTabChange}
      />

      {mapOpen && (
        <DeviceMapDrawer
          isOpen={mapOpen}
          onClose={() => setMapOpen(false)}
          devices={firmDevices}
        />
      )}
    </>
  );
}

export default React.memo(ActiveDevicesCard);
